import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Copy } from 'lucide-react';
import { LandingPageConfig } from '../types';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  text-align: center;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 10px;
  font-family: 'Inter', sans-serif;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  opacity: 0.9;
  font-family: 'Inter', sans-serif;
`;

const Content = styled.div`
  padding: 30px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
`;

const UrlPreview = styled.div`
  font-size: 14px;
  color: #667eea;
  font-weight: 500;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
`;

const Actions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const BaseButton = styled.button<{
  variant?: 'primary' | 'secondary'
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  padding: 12px 16px;
  font-size: 14px;
  min-height: 40px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #667eea;
          border: 2px solid #667eea;
          &:hover:not(:disabled) {
            background: #667eea;
            color: white;
          }
        `;
      default:
        return '';
    }
  }}
`;

interface DuplicateModalProps {
  sourcePage: LandingPageConfig;
  allPages: LandingPageConfig[];
  onClose: () => void;
  onDuplicate: (name: string, slug: string) => Promise<void>;
}

const DuplicateModal: React.FC<DuplicateModalProps> = ({
  sourcePage,
  allPages,
  onClose,
  onDuplicate
}) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Format slug and validate in real-time
  const formatSlug = (inputSlug: string): string => {
    return inputSlug
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const validateSlug = (inputSlug: string): { isValid: boolean; error: string } => {
    const formatted = formatSlug(inputSlug);

    if (!formatted || formatted.trim() === '' || formatted.trim() === '-') {
      return { isValid: false, error: 'Please enter a valid custom URL' };
    }

    // Check for conflicts with other pages
    const conflictingPage = allPages.find(p => p.slug === formatted && p.id !== sourcePage.id);
    if (conflictingPage) {
      return { isValid: false, error: 'This URL slug is already taken by another page. Please choose a different one.' };
    }

    return { isValid: true, error: '' };
  };

  // Update slug with formatting and validation
  const handleSlugChange = (value: string) => {
    const formatted = formatSlug(value);
    setSlug(formatted);

    const validation = validateSlug(value);
    setSlugError(validation.error);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert('Please enter a page name');
      return;
    }

    const validation = validateSlug(slug);
    if (!validation.isValid) {
      setSlugError(validation.error);
      return;
    }

    setIsLoading(true);
    try {
      await onDuplicate(name.trim(), slug);
      onClose();
    } catch (error) {
      console.error('Duplicate failed:', error);
      alert(`Failed to create duplicate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <Header>
          <HeaderTitle><Copy size={24} style={{ marginRight: '10px' }} />Duplicate Landing Page</HeaderTitle>
          <HeaderSubtitle>Choose a new name and URL for the duplicate</HeaderSubtitle>
        </Header>

        <Content>
          <InputGroup>
            <Label>Page Name</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter a name for your new landing page"
              autoFocus
            />
          </InputGroup>

          <InputGroup>
            <Label>Custom URL</Label>
            <Input
              type="text"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="choose-a-custom-url"
            />
            {slug && !slugError && (
              <UrlPreview>
                Your landing page will be accessible at: /{slug}
              </UrlPreview>
            )}
            {slugError && (
              <ErrorMessage>{slugError}</ErrorMessage>
            )}
          </InputGroup>

          <Actions>
            <BaseButton variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </BaseButton>
            <BaseButton
              variant="primary"
              onClick={handleSubmit}
              disabled={isLoading || !!slugError || !name.trim()}
            >
              {isLoading ? 'Creating...' : 'Create Duplicate'}
            </BaseButton>
          </Actions>
        </Content>
      </ModalContent>
    </ModalOverlay>
  );
};

export default DuplicateModal;